import axios, { AxiosResponse } from 'axios';
import { UserProps } from '../types/user_types.ts';
import {
    ApiTokenRetrievalError,
    fetchAPIKnoxToken,
} from './di_api_functions.ts';

export enum AuthMeStatus {
    AUTHENTICATED,
    UNAUTHENTICATED,
    UNAVAILABLE,
    UNKNOWN_STATE,
}

type UserClaim = { typ: string; val: string };

export class AuthMeNotAvailable extends Error {
    constructor() {
        super('Auth me is not available');
        this.name = 'AuthMeNotAvailable';
        Object.setPrototypeOf(this, AuthMeNotAvailable.prototype);
    }
}
export class AuthMeUnauthenticated extends Error {
    constructor() {
        super('Auth me access is denied because user is not authenticated');
        this.name = 'AuthMeUnauthenticated';
        Object.setPrototypeOf(this, AuthMeUnauthenticated.prototype);
    }
}

export class AuthMeIdTokenExpired extends Error {
    constructor() {
        super('Auth me ID token expired');
        this.name = 'AuthMeIdTokenExpired';
        Object.setPrototypeOf(this, AuthMeIdTokenExpired.prototype);
    }
}

export const GetAzAuthMeStatus = async (): Promise<AuthMeStatus> => {
    try {
        const response = await axios.get(`/.auth/me`, {
            validateStatus: status => status < 500,
        });
        if (response.status === 404) return AuthMeStatus.UNAVAILABLE;
        if (response.status === 401) return AuthMeStatus.UNAUTHENTICATED; // Auth me answering properly when not authenticated
        if (response.status === 200 && response.data[0].id_token)
            return AuthMeStatus.AUTHENTICATED; // Auth me answering when already authenticated
        return AuthMeStatus.UNKNOWN_STATE;
    } catch {
        return AuthMeStatus.UNKNOWN_STATE;
    }
};

type AuthMeResponse = {
    idToken: string;
    username: string;
};

export const callAadLogin = async () => {
    // send user to AAD login and redirect to the current page after login
    window.location.href = `/.auth/login/aad?post_login_redirect_url=/%23/auth/callback`;
};

const parseAuthMeResponse = (response: AxiosResponse): AuthMeResponse => {
    if (!response) {
        throw new AuthMeNotAvailable();
    }

    if (response.status == 402) {
        throw new AuthMeUnauthenticated();
    }

    if (response.status !== 200) {
        throw new AuthMeNotAvailable();
    }

    if (!response.data || response.data.length === 0) {
        throw new AuthMeNotAvailable();
    }

    const meJson = response.data[0];

    if (!meJson || !meJson.id_token) {
        throw new AuthMeNotAvailable();
    }

    const expirationClaims = meJson.user_claims?.filter(
        (claim: UserClaim) => claim.typ === 'exp',
    );

    const expirationTime =
        expirationClaims?.length > 0
            ? parseInt(expirationClaims[0].val)
            : undefined;

    if (!expirationTime || expirationTime < Date.now() / 1000) {
        throw new AuthMeIdTokenExpired();
    }

    return {
        idToken: meJson.id_token,
        username: meJson.user_id,
    };
};

export const fetchEasyAuthUserProps = async (): Promise<AuthMeResponse> => {
    let response = null;

    try {
        response = await axios.get(`/.auth/me`, {
            validateStatus: status => status < 500,
        });
    } catch {
        throw new AuthMeNotAvailable();
    }

    let authMeResponse: AuthMeResponse | null = null;
    authMeResponse = parseAuthMeResponse(response);

    return authMeResponse;
};

export const fetchUserProps = async (): Promise<UserProps> => {
    const authMeResponse = await fetchEasyAuthUserProps();
    const token = await fetchAPIKnoxToken(authMeResponse.idToken);
    if (token == null) {
        throw new ApiTokenRetrievalError();
    }
    return {
        username: authMeResponse.username,
        token: token.token,
        tokenExpiration: token.expiry,
    };
};
